import React, { useEffect, useState } from "react";
import Header from "../../components/header";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  generateContentKeyForAssessment,
  MODEL_TYPE_CAREER_PREFERENCE,
} from "../../utils/general-config";

import { generateContentKeyForCareerPreference } from "../../utils/assessment-utils";
import {
  convertToUnderscore,
  getCookie,
  getEnneagramBannerImage,
  getIndustryBannerImage,
  getIndustryImage,
} from "../../utils/utility-functions";

import Loader from "../../components/loader";

import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const CareerPreferencePrecontent = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { t } = useTranslation();
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);

  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender, assessmentId, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const careerPreferencePath = PathHelper(
    APP_NAVIGATION.CAREER_PREFERENCE_REPORT,
    currentLocale
  );
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  const traits = generateContentKeyForCareerPreference(
    userTraits,
    currentAssessmentAttemptData.assessmentId,
    currentLocale
  );

  const topTrait = traits.primary;
  const topFourTraitTitles = traits.secondary;
  const industryImage = contentData?.industry
    ? getIndustryBannerImage(convertToUnderscore(topTrait.en))
    : null;

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }
        const contentKey = generateContentKeyForAssessment(
          traitsResponse?.userTraits,
          traitsResponse?.assessmentId
        );

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_CAREER_PREFERENCE[currentLocale] ||
            MODEL_TYPE_CAREER_PREFERENCE[LOCALE.EN],
          contentKey: contentKey,
        });
        if (contentResponse) {
          const { content } = contentResponse;

          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const bannerImage = getEnneagramBannerImage();

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const TakeTestButton = ({
    onClick,
    text = "TAKE TEST",
    variant = "outlined",
    clr = "#fe6635",
  }) => {
    const isFilled = variant === "filled";

    return (
      <button
        onClick={onClick}
        className={`flex items-center gap-2 px-6 py-3 font-semibold rounded-full transition-all 
          ${isFilled ? "text-white" : ""}
          ${isFilled ? "" : "bg-white hover:text-white"}
        `}
        style={{
          backgroundColor: isFilled ? clr : "#fff",
          color: isFilled ? "#fff" : clr,
          border: `2px solid ${clr}`,
          ...(isFilled
            ? {}
            : {
                transition: "all 0.3s ease",
                ":hover": {
                  backgroundColor: clr,
                  color: "#fff",
                },
              }),
        }}
      >
        {text}
        <svg
          fill={isFilled ? "white" : clr}
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />
        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full px-8 py-12">
            {/* Title & Button Wrapper */}
            <div
              className="flex flex-col md:flex-row items-start justify-between gap-4"
              id={reportType}
            >
              {/* Title */}
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-4xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const GradientCard = ({ text = "", testText, onClick, industryImage }) => {
    return (
      <div className="w-full md:min-h-[200px] flex flex-col justify-between items-center p-4 md:p-6 rounded-2xl shadow-lg text-start bg-[#f8f4fe]">
        <img
          className="w-16 h-16 mt-2 mb-4"
          src={industryImage}
          style={{
            filter:
              "invert(33%) sepia(100%) saturate(748%) hue-rotate(217deg) brightness(92%) contrast(90%)",
          }}
          alt="icon"
        />
        <h2 className="text-[#805bec] text-xl font-bold text-center mb-4">
          {snakeToTitleCase(text)}
        </h2>
        <TakeTestButton onClick={onClick} text={testText} />
      </div>
    );
  };

  function snakeToTitleCase(snakeStr) {
    return snakeStr
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize first letter
      .join(" ");
  }

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading && (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          )}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <HeroBanner
              title={t(
                "report.career_preference_precontent.task_based_career_results"
              )}
              color="blue"
            />
            {isAuth && (
              <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                <ShareButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData}
                  userGUID={userGUID}
                  userData={userData}
                />
                <SendToFriendButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData}
                  userGUID={userGUID}
                  userData={userData}
                  CreateGuestUserConnect={sendToFriendReportOnEmailConnect}
                />
              </div>
            )}
            <div className="w-full p-4 md:px-8 md:py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                <div>
                  <h2 className="text-gray-700 text-2xl font-semibold">
                    {t("report.career_preference_precontent.primary_industry")}
                  </h2>
                  <h2 className="text-[#805bec] text-3xl my-4 font-bold">
                    {contentData?.industry}
                  </h2>
                  <p className="text-gray-700 text-base mt-6">
                    {contentData?.intro}
                  </p>
                  <div className="flex items-center justify-between mt-6">
                    <TakeTestButton
                      text={t(
                        "report.career_preference_precontent.view_full_report_cta"
                      )}
                      bgNeeded={true}
                      variant="filled"
                      onClick={() => {
                        navigate(
                          `${careerPreferencePath.next}?code=${topTrait.en}&aai=${assessmentAttemptId}&uid=${userId}`
                        );
                      }}
                    />
                  </div>
                </div>
                {/* Image Section - Adjusts Dynamically */}
                {industryImage ? (
                  <div className="flex-1 flex items-center justify-center w-full min-h-[200px]">
                    <img
                      src={industryImage}
                      alt="Industry Overview"
                      className="w-full max-w-full h-auto object-contain"
                    />
                  </div>
                ) : null}
              </div>
              <h2 className="text-gray-700 text-2xl mt-6 font-semibold">
                {t("report.career_preference_precontent.secondary_industry")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mt-6">
                {topFourTraitTitles?.map((item) => {
                  const industryImage = getIndustryImage(
                    convertToUnderscore(item?.en)
                  );
                  return (
                    <GradientCard
                      key={item}
                      industryImage={industryImage}
                      text={item?.localized}
                      testText={t(
                        "report.career_preference_precontent.view_more_into_cta"
                      )}
                      onClick={() => {
                        navigate(
                          `${careerPreferencePath.next}?code=${item.en}&aai=${assessmentAttemptId}&uid=${userId}`
                        );
                      }}
                    />
                  );
                })}
              </div>
              {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mt-6">
                {topFourTraitTitles?.map((item, index) => (
                  <GradientCard
                    text={item}
                    testText={"View More Info"}
                    onClick={() => {
                      navigate(
                        `${careerPreferencePath.next}?code=${item}&aai=${assessmentAttemptId}&uid=${userId}`
                      );
                    }}
                  />
                ))}
              </div> */}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CareerPreferencePrecontent;
